import React, { useState, useEffect, useCallback } from "react";
import CustomSessionForm from "./CustomSessionForm";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  List, // Import List components
  ListItem,
  ListItemText,
  Divider,
  Collapse, // Import Collapse for expandable content
  IconButton, // Import IconButton for toggle icon
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import {
  ExpandMore as ExpandMoreIcon, // Import ExpandMoreIcon
  ExpandLess as ExpandLessIcon, // Import ExpandLessIcon
} from "@material-ui/icons"; // Import icons
import { functions, firestore, auth } from "firebase/firebase.utils"; // Import Firebase functions service, firestore, AND auth
import { httpsCallable } from "firebase/functions"; // Import httpsCallable to call Cloud Functions
import { onAuthStateChanged } from "firebase/auth"; // Import onAuthStateChanged
import {
  doc, // Import doc
  onSnapshot as onDocSnapshot, // Import onSnapshot for single doc listener
  collection,
  query,
  onSnapshot,
  orderBy,
  where,
} from "firebase/firestore"; // Import Firestore query functions including 'where'
import CustomSessionQuestionItem from "./CustomSessionQuestionItem";
import ShowcaseModal from "./ShowcaseModal"; // Import the new modal

const CustomSessionPage = ({ currentUser, analyzer }) => {
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Loading for generating NEW session / loading questions
  const [error, setError] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null); // Track loaded/generated session
  const [isPastSessionsCardExpanded, setIsPastSessionsCardExpanded] =
    useState(true); // State for past sessions card expansion
  // State for expanded questions in test mode
  const [expandedQuestions, setExpandedQuestions] = useState({});
  // State to hold the Firestore listener unsubscribe function for questions
  const [unsubscribeQuestionsListener, setUnsubscribeQuestionsListener] =
    useState(null);
  // State to hold the list of user's past sessions
  const [userSessions, setUserSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false); // Loading for fetching session list

  // --- New State for User Auth and Data ---
  const [authStatus, setAuthStatus] = useState("loading"); // 'loading', 'anonymous', 'loggedIn'
  const [userData, setUserData] = useState({
    isPremium: false,
    credits: 0,
    hasUsedShowcaseSession: false,
    // Add other relevant fields if needed later
  });
  const [unsubscribeUserListener, setUnsubscribeUserListener] = useState(null); // Listener for user doc
  const [showShowcaseModal, setShowShowcaseModal] = useState(false); // For anonymous users
  // --- End New State ---
  // Cleanup listeners on component unmount
  useEffect(() => {
    return () => {
      if (unsubscribeQuestionsListener) {
        console.log("Cleaning up Firestore questions listener on unmount");
        unsubscribeQuestionsListener();
      }
      if (unsubscribeUserListener) {
        // Add cleanup for user listener
        console.log("Cleaning up Firestore user listener on unmount");
        unsubscribeUserListener();
      }
    };
  }, [unsubscribeQuestionsListener, unsubscribeUserListener]); // Add user listener cleanup dependency

  // Effect to fetch user's past sessions
  // Effect to listen for Auth changes and fetch user data
  useEffect(() => {
    // Cleanup previous user listener if it exists
    if (unsubscribeUserListener) {
      unsubscribeUserListener();
    }

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("User logged in:", user.uid);
        setAuthStatus("loggedIn");

        // Setup listener for the user document
        const userDocRef = doc(firestore, "users", user.uid);
        const userListener = onDocSnapshot(
          userDocRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              console.log("User data updated:", data);
              setUserData({
                isPremium: data.isPremium ?? false,
                credits: data.credits ?? 0,
                hasUsedShowcaseSession: data.hasUsedShowcaseSession ?? false,
                // Add other fields as needed
              });
            } else {
              // This case should ideally not happen if createUserProfileDocument works
              console.error(
                "User document not found in Firestore for logged-in user:",
                user.uid
              );
              setUserData({
                isPremium: false,
                credits: 0,
                hasUsedShowcaseSession: false,
              }); // Reset to default non-premium
            }
          },
          (error) => {
            console.error("Error listening to user document:", error);
            setError("Failed to load user profile data.");
            setUserData({
              isPremium: false,
              credits: 0,
              hasUsedShowcaseSession: false,
            }); // Reset on error
          }
        );
        setUnsubscribeUserListener(() => userListener); // Store the unsubscribe function
      } else {
        console.log("User logged out or anonymous.");
        setAuthStatus("anonymous");
        setUserData({
          isPremium: false,
          credits: 0,
          hasUsedShowcaseSession: false,
        }); // Reset user data
        // Cleanup user listener if it exists when user logs out
        if (unsubscribeUserListener) {
          unsubscribeUserListener();
          setUnsubscribeUserListener(null);
        }
      }
    });

    // Cleanup auth listener on unmount
    return () => unsubscribeAuth();
  }, []); // Run only once on mount
  // Effect to fetch user's past sessions (Modified to use authStatus)
  useEffect(() => {
    // We need the actual user ID from auth state, not just the prop
    const currentUserId = auth.currentUser?.uid;

    if (authStatus !== "loggedIn" || !currentUserId) {
      // Check authStatus and actual UID
      setUserSessions([]); // Clear sessions if user logs out or is anonymous
      setIsLoadingSessions(false); // Ensure loading stops
      return;
    }

    setIsLoadingSessions(true);
    const sessionsCollectionRef = collection(firestore, "CustomSessions");
    // Query for sessions owned by the current user, order by creation date descending
    const q = query(
      sessionsCollectionRef,
      where("userId", "==", currentUserId), // Use currentUserId from auth state
      orderBy("createdAt", "desc")
    );

    const unsubscribeSessions = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedSessions = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUserSessions(fetchedSessions);
        setIsLoadingSessions(false);
      },
      (error) => {
        console.error("Error fetching user sessions:", error);
        setError("Failed to load past sessions.");
        setIsLoadingSessions(false);
      }
    );

    // Cleanup sessions listener
    return () => unsubscribeSessions();
  }, [authStatus]); // Re-run only when authStatus changes

  // Function to setup listener for questions of a specific session
  const setupQuestionsListener = useCallback(
    (sessionId) => {
      // Cleanup previous listener first
      if (unsubscribeQuestionsListener) {
        unsubscribeQuestionsListener();
      }

      console.log(`Setting up listener for questions in session ${sessionId}`);
      const questionsCollectionRef = collection(
        firestore,
        `CustomSessions/${sessionId}/questions`
      );
      const q = query(questionsCollectionRef, orderBy("dateCreation", "asc")); // Assuming dateCreation exists

      const listener = onSnapshot(
        q,
        (querySnapshot) => {
          const fetchedQuestions = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("Fetched questions:", fetchedQuestions);
          setGeneratedQuestions(fetchedQuestions);
          setCurrentSessionId(sessionId); // Track the currently loaded session
          setExpandedQuestions({}); // Reset expanded state for new questions
          setIsLoading(false); // Stop loading indicator
        },
        (error) => {
          console.error("Error fetching custom session questions:", error);
          setError("Failed to fetch questions for the session.");
          setGeneratedQuestions([]);
          setIsLoading(false);
        }
      );

      // Store the new unsubscribe function
      setUnsubscribeQuestionsListener(() => listener);
    },
    [unsubscribeQuestionsListener]
  ); // Include listener state in dependencies

  // Function to handle GENERATING a NEW session (Updated with Auth/Credit Checks)
  const handleGenerateSession = async (topic, difficulties) => {
    setError(null); // Clear previous errors

    // 1. Check Auth Status
    if (authStatus === "loading") {
      console.log("Auth status loading, cannot generate yet.");
      return; // Or show a specific message
    }

    if (authStatus === "anonymous") {
      console.log("Anonymous user attempting to generate session.");
      setShowShowcaseModal(true); // Show the showcase modal
      return;
    }

    // 2. Check User Data and Permissions (Logged-in users)
    if (!userData.isPremium && userData.hasUsedShowcaseSession) {
      console.log("Non-premium user already used showcase.");
      setError(
        "Vous avez déjà utilisé votre session de démonstration gratuite. Passez à premium pour en générer d'autres."
      );
      return;
    }

    if (userData.isPremium && userData.credits < 4) {
      console.log("Premium user has insufficient credits.");
      setError(
        "Crédits insuffisants. Vous avez besoin d'au moins 4 crédits pour générer une session premium. Veuillez recharger."
      );
      // TODO: Add link/button to purchase credits
      return;
    }

    // 3. Proceed with generation if checks pass

    // Cleanup previous listener if it exists
    if (unsubscribeQuestionsListener) {
      unsubscribeQuestionsListener();
      setUnsubscribeQuestionsListener(null);
    }

    setIsLoading(true);
    setGeneratedQuestions([]);
    setExpandedQuestions({});
    setCurrentSessionId(null); // Clear current session ID

    try {
      const generateCustomSessionQuestionsCallable = httpsCallable(
        functions,
        "generateCustomSessionQuestions"
      );
      // Backend expects count parameter even though it determines the actual count based on user status
      const payload = {
        topic,
        difficulty: difficulties[0] || "Junior",
        count: 30, // Keep count in payload - backend uses it for validation but determines final count
      };
      console.log(
        "Calling generateCustomSessionQuestions with payload (count determined by backend):",
        payload
      );
      const result = await generateCustomSessionQuestionsCallable(payload);

      if (result.data.success && result.data.sessionId) {
        console.log(`Session ${result.data.sessionId} created successfully.`);
        // Setup listener for the newly created session's questions
        setupQuestionsListener(result.data.sessionId);
        // setIsLoading(false) is handled by setupQuestionsListener
      } else {
        // Handle specific errors from the backend if available
        const backendError =
          result.data.error || "Failed to create session or get session ID.";
        console.error("Backend function returned an error:", backendError);
        throw new Error(backendError);
      }
    } catch (err) {
      console.error("Error in handleGenerateSession:", err);
      // Display specific HttpsError messages directly if they exist
      const message =
        err.message || "Failed to generate custom session. Please try again.";
      setError(message);
      setGeneratedQuestions([]);
      setIsLoading(false); // Ensure loading stops on error
    }
  };

  // Function to handle LOADING an EXISTING session
  const handleLoadSession = (sessionId) => {
    console.log("Load session:", sessionId);
    if (currentSessionId === sessionId) return; // Don't reload if already loaded

    setIsLoading(true); // Show loading indicator while fetching
    setError(null);
    setGeneratedQuestions([]);
    setExpandedQuestions({});
    // Setup listener for the selected session's questions
    setupQuestionsListener(sessionId);
  };

  // Function to toggle expanded state for a question
  const toggleExpanded = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  // Function to handle user's answer submission in test mode
  const handleAnswerSubmit = (questionId, userAnswer) => {
    console.log(`User submitted answer for ${questionId}: ${userAnswer}`);
    // TODO: Implement logic to compare user answer with correct answer and provide feedback
    toggleExpanded(questionId); // Reveal answer after submit attempt
  };

  return (
    <div
      className="custom-session-page"
      style={{
        padding: "24px",
        paddingTop: "80px", // Added to account for floating navbar
        backgroundColor: "#F6F7F6",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Display User's Past Sessions */}
      {currentUser && (
        <Card
          style={{
            width: "100%",
            maxWidth: "960px",
            marginBottom: "24px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          }}
        >
          <CardHeader
            title={
              <Typography
                variant="h5"
                style={{ fontWeight: "bold", color: "#1976d2" }}
              >
                Vos sessions passées
              </Typography>
            }
            action={
              <IconButton
                aria-label="toggle past sessions"
                onClick={() =>
                  setIsPastSessionsCardExpanded(!isPastSessionsCardExpanded)
                }
              >
                {isPastSessionsCardExpanded ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />
          <Collapse
            in={isPastSessionsCardExpanded}
            timeout="auto"
            unmountOnExit
          >
            <CardContent>
              {isLoadingSessions ? (
                <Box display="flex" justifyContent="center">
                  <CircularProgress size={30} />
                </Box>
              ) : userSessions.length > 0 ? (
                <List component="nav" aria-label="user sessions" dense>
                  {userSessions.map((session, index) => (
                    <React.Fragment key={session.id}>
                      <ListItem
                        button
                        onClick={() => handleLoadSession(session.id)}
                        selected={currentSessionId === session.id}
                      >
                        <ListItemText
                          primary={`Session: ${
                            session.topic || "Unknown Topic"
                          }`}
                          secondary={`Created: ${
                            session.createdAt?.toDate().toLocaleDateString() ||
                            "Unknown Date"
                          }`}
                        />
                      </ListItem>
                      {index < userSessions.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  style={{ textAlign: "center", color: "#757575" }}
                >
                  Vous n'avez pas encore généré de sessions personnalisées.
                </Typography>
              )}
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Session Generation Card */}
      <Card
        style={{
          width: "100%",
          maxWidth: "960px",
          marginBottom: "16px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        }}
      >
        <CardHeader
          title={
            <Typography
              variant="h4"
              style={{ fontWeight: "bold", color: "#1976d2" }}
            >
              Générer une nouvelle session
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" style={{ color: "#757575" }}>
              Sélectionnez le sujet et la difficulté pour commencer.
            </Typography>
          }
          style={{ textAlign: "center" }}
        />
        <CardContent
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Pass user status and data to the form */}
          <CustomSessionForm
            onSubmit={handleGenerateSession}
            isLoading={isLoading || authStatus === "loading"} // Also disable form while auth is loading
            authStatus={authStatus}
            userData={userData}
            setError={setError} // Allow form to potentially set errors too
          />
          {/* Display credits for logged-in users */}
          {authStatus === "loggedIn" && (
            <Typography
              variant="body2"
              style={{ marginTop: "10px", color: "#555" }}
            >
              Crédits restants : {userData.credits}
            </Typography>
          )}
          {error &&
            !isLoading && ( // Show error only if not loading (errors set by handleGenerateSession)
              <Alert
                severity="error"
                style={{ marginTop: "16px", width: "100%", maxWidth: "600px" }}
              >
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}
        </CardContent>
      </Card>

      {/* Loading Indicator for Questions */}
      {isLoading && (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            width: "100%",
            maxWidth: "960px",
            marginTop: "16px",
          }}
        >
          <CircularProgress
            style={{ color: "#1976d2", marginBottom: "16px" }}
            size={60}
          />
          <Typography style={{ color: "#757575" }}>
            {currentSessionId
              ? "Chargement des questions de la session..."
              : "Générer vos questions d'entretien personnalisées..."}
          </Typography>
        </Box>
      )}

      {/* Display Generated/Loaded Questions (with Fake Question Logic) */}
      {!isLoading && generatedQuestions.length > 0 && (
        <Box style={{ width: "100%", maxWidth: "960px", marginTop: "16px" }}>
          <Typography
            variant="h5"
            style={{
              marginBottom: "16px",
              fontWeight: "bold",
              color: "#1976d2",
            }}
          >
            Session Questions{" "}
            {currentSessionId
              ? `(ID: ${currentSessionId.substring(0, 5)}...)`
              : ""}
          </Typography>
          {(() => {
            // Determine if we need to show fake questions (Showcase session for non-premium)
            const isShowcaseSession =
              authStatus === "loggedIn" &&
              !userData.isPremium &&
              generatedQuestions.length === 7; // Assuming backend sends exactly 7 for showcase

            let questionsToDisplay = [...generatedQuestions];

            if (isShowcaseSession) {
              // Generate 18 fake questions
              const fakeQuestions = Array.from({ length: 18 }, (_, i) => ({
                id: `fake-${i}`,
                libelle: `Question Premium ${i + 1}`, // Blurry title handled in item
                isFake: true, // Flag for the item component
                // Add other necessary placeholder fields if CustomSessionQuestionItem expects them
                difficulty: generatedQuestions[0]?.difficulty || "Junior", // Use difficulty from real questions
                tags: generatedQuestions[0]?.tags || [], // Use tags from real questions
              }));
              questionsToDisplay = [...generatedQuestions, ...fakeQuestions];
            }

            return questionsToDisplay.map((question, index) => (
              <CustomSessionQuestionItem
                key={question.id}
                question={question}
                isFake={question.isFake || false} // Pass the fake flag
                index={index}
                currentUser={currentUser}
                expanded={expandedQuestions[question.id] || false}
                toggleExpanded={toggleExpanded}
                onAnswerSubmit={handleAnswerSubmit}
                isLoading={isLoading}
              />
            ));
          })()}
        </Box>
      )}

      {/* Empty State (No session loaded/generated and not loading) */}
      {!isLoading &&
        generatedQuestions.length === 0 &&
        !error &&
        !currentSessionId && (
          <Card
            style={{
              width: "100%",
              maxWidth: "960px",
              marginTop: "16px",
              backgroundColor: "#ffffff",
              border: "2px dashed #cccccc",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <CardContent
              style={{ padding: "40px", textAlign: "center", color: "#757575" }}
            >
              <Typography variant="h6" style={{ marginBottom: "16px" }}>
                Créez une nouvelle session ou chargez une session précédente
                pour commencer !
              </Typography>
              <Typography variant="body2">
                Obtenez des questions sur mesure avec des explications et des
                niveaux de difficulté.
              </Typography>
            </CardContent>
          </Card>
        )}

      {/* Premium Upsell */}
      {!currentUser?.isPremium && (
        <Box
          style={{
            marginTop: "24px",
            width: "100%",
            maxWidth: "960px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            style={{ color: "#1976d2", marginBottom: "8px" }}
          >
            Déverrouiller l'accès complet
          </Typography>
          <Typography variant="body2" style={{ color: "#757575" }}>
            Devenez membre premium pour accéder à toutes les réponses et
            fonctionnalités.
          </Typography>
          {/* Payment button/link will go here */}
        </Box>
      )}

      {/* Render the Showcase Modal */}
      <ShowcaseModal
        open={showShowcaseModal}
        handleClose={() => setShowShowcaseModal(false)}
      />
    </div>
  );
};

export default CustomSessionPage;
